// src/pages/user/UserLearnWeek.tsx
// Routes: /user/learn/:childId/:weekKey             the week: its goal and lessons
//         /user/learn/:childId/:weekKey?next=1      straight into the next lesson
//         /user/learn/:childId/:weekKey/:lessonKey  one lesson, one step at a time
//
// A lesson is four steps, one screen each, with one thing to do on each:
//   1. Get ready   what you'll learn, what to have ready, what you'll make
//   2. Watch       the video and nothing else
//   3. Do          the activity. Lessons with an in-app workspace (Scratch or
//                  the code editor) open it from here, full screen, with the
//                  steps beside the work; "Hand in" there comes back to step 4
//   4. Hand in     the evidence and the three questions (and the project)
// After handing in: waiting for the tutor, then the feedback and the next lesson.

import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Link, Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  ArrowLeft,
  ArrowRight,
  Blocks,
  BookOpenText,
  Camera,
  Check,
  CheckCircle2,
  Clock,
  FileText,
  FileUp,
  Link2,
  ListChecks,
  LifeBuoy,
  MapPin,
  Mic,
  Package,
  Play,
  RotateCcw,
  Sparkles,
  Sprout,
  Star,
  Target,
  Upload,
  Users,
  Video,
  X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useChildMode } from "../../app/childMode";
import { selectUser } from "../../app/features/authSlice";
import {
  RUBRIC_LABELS,
  SCORE_LABELS,
  errorText,
  putFile,
  useGetEvidenceUploadUrlMutation,
  useGetLearnMediaUrlMutation,
  useGetLearnWeekQuery,
  useGetLessonVideoUrlMutation,
  useMarkFeedbackSeenMutation,
  useMarkWatchedMutation,
  useSendStuckMutation,
  useSubmitWorkMutation,
} from "../../app/api/learningApi";
import type {
  Attempt,
  Evidence,
  EvidenceType,
  LearnLesson,
  LearnWeek,
  Reflection,
  Review,
  WorkStatus,
} from "../../app/api/learningApi";
import { ProgressBar, fmtDay } from "./UserLearn";
import MediaPlayer from "../../components/MediaPlayer";
import { ProjectViewer, WorkspaceOverlay } from "../../components/workspace";
import { PROJECT_NOUN, ProjectAttach, WORKSPACE_TEXT, WorkspaceCard } from "../../components/workspace/LessonWorkspace";

/* ─── shared pieces ──────────────────────────────────────────── */

export const STATUS: Record<WorkStatus, { label: string; cls: string }> = {
  not_started: { label: "Not started", cls: "bg-[#f1f3f8] text-[#5f6780]" },
  in_progress: { label: "Watched", cls: "bg-[#eaf1fb] text-[#1a4a7a]" },
  submitted: { label: "With your tutor", cls: "bg-[#fff8ee] text-[#7a4a10]" },
  needs_revision: { label: "Try again", cls: "bg-[#fff4f1] text-[#7a3020]" },
  accepted: { label: "Accepted", cls: "bg-[#edfaf4] text-[#2a7a56]" },
};

const EVIDENCE: Record<EvidenceType, { label: string; icon: LucideIcon }> = {
  photo: { label: "Photo", icon: Camera },
  video: { label: "Video", icon: Video },
  audio: { label: "Voice note", icon: Mic },
  link: { label: "Link", icon: Link2 },
  file: { label: "File", icon: FileUp },
  text: { label: "Written answer", icon: FileText },
  project: { label: "Project", icon: Blocks },
};

const ACCEPT: Partial<Record<EvidenceType, string>> = {
  photo: "image/*",
  video: "video/*",
  audio: "audio/*",
  file: "*/*",
};

const input =
  "w-full rounded-2xl border border-[#dde2ec] bg-white px-4 py-3 text-[15px] text-[#16213e] outline-none transition focus:border-[#1b2d5e] focus:ring-4 focus:ring-[#e6ebf5]";
const primaryBtn =
  "inline-flex items-center justify-center gap-2 rounded-2xl bg-[#1b2d5e] px-6 py-3.5 text-[15px] font-bold text-white shadow-[0_8px_24px_rgba(27,45,94,0.25)] transition hover:bg-[#24397a] disabled:opacity-50";
const quietBtn =
  "inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm font-semibold text-[#3b4560] transition hover:bg-[#ebeff7]";

const minutes = (n: number) => `${n} minute${n === 1 ? "" : "s"}`;

/** Fetches a signed url on demand and shows the file. */
function MediaView({ childId, lessonKey, ev }: { childId: string; lessonKey: string; ev: Evidence }) {
  const [url, setUrl] = useState<string | null>(null);
  const [getUrl, { isLoading, error }] = useGetLearnMediaUrlMutation();
  if (ev.type === "text")
    return <p className="whitespace-pre-wrap rounded-2xl bg-[#f9fafc] p-4 text-sm leading-6 text-[#16213e]">{ev.text}</p>;
  if (ev.type === "link")
    return (
      <a href={ev.url ?? "#"} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 break-all text-sm font-semibold text-[#1b2d5e]">
        <Link2 className="h-4 w-4 flex-shrink-0" /> {ev.url}
      </a>
    );
  if (!ev.key) return null;
  const Icon = EVIDENCE[ev.type].icon;
  if (ev.type === "project") return <ProjectButton childId={childId} lessonKey={lessonKey} ev={ev} />;
  if (!url)
    return (
      <button
        type="button"
        onClick={async () => {
          try {
            setUrl((await getUrl({ childId, lessonKey, key: ev.key! }).unwrap()).data.url);
          } catch {
            /* shown below */
          }
        }}
        className="inline-flex items-center gap-2 rounded-xl bg-[#f1f3f8] px-3 py-2 text-sm font-semibold text-[#1b2d5e]"
      >
        <Icon className="h-4 w-4" />
        {isLoading ? "Opening…" : `Open ${EVIDENCE[ev.type].label.toLowerCase()}${ev.fileName ? `: ${ev.fileName}` : ""}`}
        {error && <span className="ml-2 text-[#b94a3a]">{errorText(error, "")}</span>}
      </button>
    );
  if (ev.type === "photo") return <img src={url} alt={ev.fileName ?? "Your work"} className="max-h-80 rounded-2xl" />;
  if (ev.type === "video") return <MediaPlayer src={url} className="max-w-lg" />;
  if (ev.type === "audio") return <MediaPlayer src={url} kind="audio" />;
  return (
    <a href={url} target="_blank" rel="noreferrer" className="text-sm font-semibold text-[#1b2d5e]">
      Download {ev.fileName}
    </a>
  );
}

/** A handed-in project: opens the copy that went to the tutor, read-only. */
function ProjectButton({ childId, lessonKey, ev }: { childId: string; lessonKey: string; ev: Evidence }) {
  const [open, setOpen] = useState(false);
  const [getUrl] = useGetLearnMediaUrlMutation();
  const kind = ev.projectKind ?? "scratch";
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 rounded-xl bg-[#f1f3f8] px-3 py-2 text-sm font-semibold text-[#1b2d5e]"
      >
        <Blocks className="h-4 w-4" /> Open {kind === "scratch" ? "the Scratch project" : kind === "web" ? "the code" : `the ${PROJECT_NOUN[kind]}`}{ev.fileName ? `: ${ev.fileName}` : ""}
      </button>
      {open && (
        <ProjectViewer
          kind={kind}
          name={ev.fileName ?? "Project"}
          subtitle="What you handed in"
          getUrl={async () => (await getUrl({ childId, lessonKey, key: ev.key! }).unwrap()).data.url}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function Stars({ n }: { n: number }) {
  return (
    <span className="inline-flex" aria-label={`${n} of 3`}>
      {[1, 2, 3].map((i) => (
        <Star key={i} className={`h-3.5 w-3.5 ${i <= n ? "fill-[#c9a227] text-[#c9a227]" : "text-[#cdd4e2]"}`} />
      ))}
    </span>
  );
}

function FeedbackCard({ review, childId, lessonKey }: { review: Review; childId: string; lessonKey: string }) {
  const accepted = review.decision === "accepted";
  const scores = Object.entries(review.scores ?? {}).filter(([, v]) => v);
  return (
    <div className={`rounded-[24px] border p-5 sm:p-6 ${accepted ? "border-[#b6e8d0] bg-[#edfaf4]" : "border-[#f5c5b0] bg-[#fff4f1]"}`}>
      <div className="flex items-center gap-3">
        {accepted ? (
          <CheckCircle2 className="h-7 w-7 flex-shrink-0 text-[#2a7a56]" />
        ) : (
          <RotateCcw className="h-7 w-7 flex-shrink-0 text-[#b65a3a]" />
        )}
        <div>
          <p className={`text-lg font-bold ${accepted ? "text-[#1f5c3f]" : "text-[#7a3020]"}`}>
            {accepted ? "Accepted. Great work!" : "Your tutor would like you to try again"}
          </p>
          <p className="text-[12.5px] text-[#5f6780]">
            {review.reviewerName ?? "Your tutor"} ·{" "}
            {new Date(review.reviewedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
          </p>
        </div>
      </div>
      {review.voiceNoteKey && (
        <div className="mt-4">
          <p className="mb-2 text-sm font-semibold text-[#3b4560]">Listen to your tutor</p>
          <MediaView childId={childId} lessonKey={lessonKey} ev={{ type: "audio", key: review.voiceNoteKey, fileName: "voice note" }} />
        </div>
      )}
      <div className="mt-4 space-y-3 text-[15px] leading-6 text-[#16213e]">
        {review.glow && (
          <p className="flex gap-2.5">
            <Sparkles className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#c9a227]" />
            <span><b>What went well:</b> {review.glow}</span>
          </p>
        )}
        {review.grow && (
          <p className="flex gap-2.5">
            <Sprout className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#2a7a56]" />
            <span><b>One thing to improve:</b> {review.grow}</span>
          </p>
        )}
        {review.nextStep && (
          <p className="flex gap-2.5">
            <ArrowRight className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#1b2d5e]" />
            <span><b>Next step:</b> {review.nextStep}</span>
          </p>
        )}
      </div>
      {scores.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-1.5">
          {scores.map(([area, v]) => (
            <span key={area} className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-[12.5px] font-semibold text-[#3b4560]">
              {RUBRIC_LABELS[area] ?? area} <Stars n={v as number} /> {SCORE_LABELS[v as number]}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Step 1: Get ready ──────────────────────────────────────── */

function ReadyStep({ lesson, isParent }: { lesson: LearnLesson; isParent: boolean }) {
  const total = (lesson.videoMinutes ?? lesson.watchMinutes) + lesson.doMinutes + lesson.uploadMinutes;
  return (
    <div className="space-y-5">
      {lesson.objective && (
        <div className="flex gap-3 rounded-[22px] bg-[#f1f3f8] p-5">
          <Target className="mt-0.5 h-6 w-6 flex-shrink-0 text-[#1b2d5e]" />
          <div>
            <p className="text-sm font-semibold text-[#5f6780]">Today you will</p>
            <p className="mt-1 text-lg font-semibold leading-7 text-[#16213e]">{lesson.objective}</p>
          </div>
        </div>
      )}

      {lesson.action.materials.length > 0 && (
        <div className="rounded-[22px] border border-[#e5e8ef] bg-white p-5">
          <p className="flex items-center gap-2 text-sm font-bold text-[#16213e]">
            <Package className="h-5 w-5 text-[#c9a227]" /> Get these ready before you start
          </p>
          <ul className="mt-3 grid gap-2 sm:grid-cols-2">
            {lesson.action.materials.map((m, i) => (
              <li key={i} className="flex items-start gap-2 text-[15px] leading-6 text-[#3b4560]">
                <Check className="mt-1 h-4 w-4 flex-shrink-0 text-[#2a7a56]" /> {m}
              </li>
            ))}
          </ul>
        </div>
      )}

      {lesson.evidence.prompt && (
        <div className="rounded-[22px] border border-[#e5e8ef] bg-white p-5">
          <p className="flex items-center gap-2 text-sm font-bold text-[#16213e]">
            <Upload className="h-5 w-5 text-[#1b2d5e]" /> At the end you will hand in
          </p>
          <p className="mt-2 text-[15px] leading-6 text-[#3b4560]">{lesson.evidence.prompt}</p>
        </div>
      )}

      <p className="flex items-center gap-2 text-sm text-[#5f6780]">
        <Clock className="h-4 w-4" /> About {minutes(total)} in all
        {lesson.action.offScreen ? ", most of it away from the screen" : ""}.
      </p>

      {isParent && lesson.parentNote && (
        <p className="flex gap-2.5 rounded-[18px] bg-[#fff8ee] px-4 py-3 text-sm leading-6 text-[#7a4a10]">
          <Users className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span><b>For you, parent:</b> {lesson.parentNote}</span>
        </p>
      )}
    </div>
  );
}

/* ─── Step 2: Watch ──────────────────────────────────────────── */

// Data saver: the 480p version, about a sixth of the data. On by default when
// the browser says data is limited; the choice is remembered on this device.
const DATA_SAVER_KEY = "fsl-data-saver";
function initialDataSaver() {
  try {
    const saved = localStorage.getItem(DATA_SAVER_KEY);
    if (saved !== null) return saved === "1";
  } catch {
    /* storage unavailable */
  }
  const c = (navigator as any).connection;
  return Boolean(c?.saveData || /(^|-)2g|3g/.test(c?.effectiveType ?? ""));
}

type LessonVideo = { url: string; quality: "low" | "high"; hasLowRes: boolean; captionsUrl: string | null; posterUrl: string | null };

function WatchStep({ childId, lesson, onWatched }: { childId: string; lesson: LearnLesson; onWatched: () => void }) {
  const [video, setVideo] = useState<LessonVideo | null>(null);
  const [dataSaver, setDataSaver] = useState(initialDataSaver);
  const [showWords, setShowWords] = useState(!lesson.hasVideo);
  const [getUrl, { isLoading, error }] = useGetLessonVideoUrlMutation();

  const load = async (low: boolean) => {
    try {
      setVideo((await getUrl({ childId, lessonKey: lesson.key, quality: low ? "low" : undefined }).unwrap()).data);
    } catch {
      /* shown below */
    }
  };

  // Fetch the (short-lived) video link as soon as this step opens, so the
  // player is already in place when the child presses play
  useEffect(() => {
    if (lesson.hasVideo) load(dataSaver);
  }, [lesson.key]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleDataSaver = () => {
    const next = !dataSaver;
    setDataSaver(next);
    try {
      localStorage.setItem(DATA_SAVER_KEY, next ? "1" : "0");
    } catch {
      /* storage unavailable */
    }
    if (video) load(next);
  };

  return (
    <div className="space-y-3">
      {lesson.hasVideo ? (
        // A fixed 16:9 frame, so nothing jumps while the player gets ready
        <div className="relative aspect-video w-full overflow-hidden rounded-[22px] bg-[#16213e]">
          {video ? (
            <MediaPlayer
              key={video.url}
              src={video.url}
              captionsUrl={video.captionsUrl}
              poster={video.posterUrl}
              onPlay={onWatched}
              speed={0.85}
              rememberSpeed="fsl-lesson-speed"
              className="h-full w-full rounded-none"
            />
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white">
              {error ? (
                <button
                  type="button"
                  onClick={() => load(dataSaver)}
                  className="rounded-2xl bg-white/15 px-5 py-2.5 text-sm font-semibold hover:bg-white/25"
                >
                  Try again
                </button>
              ) : (
                <>
                  <span className="h-10 w-10 animate-spin rounded-full border-4 border-white/25 border-t-white" aria-hidden />
                  <span className="text-sm font-semibold text-white/80">
                    {isLoading ? "Getting the video ready…" : `${minutes(lesson.videoMinutes ?? lesson.watchMinutes)}`}
                  </span>
                </>
              )}
            </div>
          )}
        </div>
      ) : (
        <p className="rounded-[22px] bg-[#f1f3f8] px-5 py-4 text-[15px] text-[#3b4560]">
          The video for this lesson is coming soon. Read the lesson below instead
          {lesson.transcript.length ? ", or ask a grown-up to read it with you" : ""}.
        </p>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        {lesson.hasVideo && (!video || video.hasLowRes) ? (
          <label className="flex cursor-pointer items-center gap-2 text-[13px] font-semibold text-[#5f6780]">
            <input type="checkbox" checked={dataSaver} onChange={toggleDataSaver} className="h-4 w-4 accent-[#1b2d5e]" />
            Data saver (480p)
          </label>
        ) : (
          <span />
        )}
        {lesson.transcript.length > 0 && (
          <button type="button" onClick={() => setShowWords((s) => !s)} className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#1b2d5e]">
            <BookOpenText className="h-4 w-4" />
            {showWords ? "Hide the words" : "Read the words instead"}
          </button>
        )}
      </div>
      {error && <p className="text-sm text-[#b94a3a]">{errorText(error, "Could not load the video")}</p>}
      {showWords && lesson.transcript.length > 0 && (
        <div className="max-h-[50vh] space-y-3 overflow-y-auto rounded-[22px] bg-white p-5 text-[15px] leading-7 text-[#16213e] ring-1 ring-[#e5e8ef]">
          {lesson.transcript.map((p, i) => <p key={i}>{p}</p>)}
        </div>
      )}
    </div>
  );
}

/* ─── Step 3: Do ─────────────────────────────────────────────── */

function DoStep({
  lesson, done, setDone, workspace,
}: {
  lesson: LearnLesson;
  done: boolean[];
  setDone: (d: boolean[]) => void;
  /** The in-app workspace card, for lessons that have one */
  workspace?: ReactNode;
}) {
  return (
    <div className="space-y-3">
      {workspace}
      {lesson.workspace && (
        <p className="rounded-2xl bg-[#f1f3f8] px-4 py-2.5 text-[13px] leading-5 text-[#3b4560]">{WORKSPACE_TEXT[lesson.workspace].stepsHint}</p>
      )}
      <p className="flex items-center gap-2 text-sm text-[#5f6780]">
        <Clock className="h-4 w-4" /> About {minutes(lesson.doMinutes)}
        {lesson.action.offScreen ? ", mostly away from the screen" : ""}. Tick each step as you finish it.
      </p>
      <ol className="space-y-2.5">
        {lesson.action.steps.map((s, i) => (
          <li key={i}>
            <label
              className={`flex cursor-pointer items-start gap-4 rounded-[20px] border p-4 transition ${
                done[i] ? "border-[#b6e8d0] bg-[#f3fbf7]" : "border-[#e5e8ef] bg-white hover:border-[#cdd4e2]"
              }`}
            >
              <input
                type="checkbox"
                className="sr-only"
                checked={done[i] ?? false}
                onChange={() => setDone(done.map((x, j) => (j === i ? !x : x)))}
              />
              <span
                className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                  done[i] ? "bg-[#2a7a56] text-white" : "bg-[#ebeff7] text-[#1b2d5e]"
                }`}
              >
                {done[i] ? <Check className="h-4 w-4" /> : i + 1}
              </span>
              <span className={`pt-1 text-[15px] leading-6 ${done[i] ? "text-[#5f6780]" : "text-[#16213e]"}`}>{s}</span>
            </label>
          </li>
        ))}
      </ol>
    </div>
  );
}

/* ─── Step 4: Hand in ────────────────────────────────────────── */

interface Draft extends Evidence {
  localId: string;
  progress?: number;
}

function HandInStep({
  childId, weekKey, lesson, storageConfigured, startFrom, onHandedIn, project, setProject,
}: {
  childId: string;
  weekKey: string;
  lesson: LearnLesson;
  storageConfigured: boolean;
  startFrom?: Attempt;
  onHandedIn: () => void;
  /** The workspace project to hand in (lessons with a workspace) */
  project: string | null;
  setProject: (id: string | null) => void;
}) {
  // A project is attached afresh (ProjectAttach), not carried over as a file
  const [items, setItems] = useState<Draft[]>(() =>
    (startFrom?.evidence ?? []).filter((e) => e.type !== "project").map((e, i) => ({ ...e, localId: `old-${i}` })),
  );
  const [reflection, setReflection] = useState<Reflection>(startFrom?.reflection ?? {});
  const [linkDraft, setLinkDraft] = useState("");
  const [textDraft, setTextDraft] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [getUploadUrl] = useGetEvidenceUploadUrlMutation();
  const [submit, { isLoading }] = useSubmitWorkMutation();
  const types = lesson.evidence.types;
  const fileTypes = types.filter((t) => ACCEPT[t]);
  const uploading = items.some((i) => i.progress !== undefined && i.progress < 100);

  const addFile = async (type: EvidenceType, file: File | undefined) => {
    if (!file) return;
    setMsg(null);
    const localId = `${Date.now()}-${Math.random()}`;
    const contentType = file.type || "application/octet-stream";
    setItems((xs) => [...xs, { localId, type, fileName: file.name, contentType, progress: 0 }]);
    try {
      const { data } = await getUploadUrl({ childId, lessonKey: lesson.key, type, fileName: file.name, contentType, sizeBytes: file.size }).unwrap();
      await putFile(data.uploadUrl, file, contentType, (p) =>
        setItems((xs) => xs.map((x) => (x.localId === localId ? { ...x, progress: Math.min(p, 99) } : x))),
      );
      setItems((xs) => xs.map((x) => (x.localId === localId ? { ...x, key: data.key, progress: 100 } : x)));
    } catch (err) {
      setItems((xs) => xs.filter((x) => x.localId !== localId));
      setMsg(errorText(err, "Upload failed"));
    }
  };

  const onSubmit = async () => {
    setMsg(null);
    const evidence: Evidence[] = items
      .filter((i) => i.type === "text" || i.type === "link" || i.key)
      .map(({ type, key, url, text, fileName, contentType }) => ({ type, key, url, text, fileName, contentType }));
    if (textDraft.trim() && types.includes("text")) evidence.push({ type: "text", text: textDraft.trim() });
    if (linkDraft.trim() && types.includes("link")) evidence.push({ type: "link", url: linkDraft.trim() });
    if (lesson.workspace && project) evidence.unshift({ type: "project", projectId: project });
    if (!evidence.length) {
      setMsg("Add your work first.");
      return;
    }
    try {
      await submit({ childId, weekKey, lessonKey: lesson.key, evidence, reflection }).unwrap();
      onHandedIn();
    } catch (err) {
      setMsg(errorText(err, "Could not hand it in"));
    }
  };

  return (
    <div className="space-y-5">
      {lesson.evidence.prompt && (
        <p className="rounded-[20px] bg-[#f1f3f8] px-5 py-4 text-[15px] font-semibold leading-6 text-[#1b2d5e]">{lesson.evidence.prompt}</p>
      )}

      {lesson.workspace && (
        <ProjectAttach childId={childId} lessonKey={lesson.key} kind={lesson.workspace} projectId={project} setProjectId={setProject} />
      )}

      {fileTypes.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2">
          {fileTypes.map((t) => {
            const Icon = EVIDENCE[t].icon;
            return (
              <label
                key={t}
                className={`flex cursor-pointer items-center gap-3 rounded-[20px] border-2 border-dashed px-5 py-4 text-[15px] font-semibold transition ${
                  storageConfigured ? "border-[#c9cfe0] text-[#1b2d5e] hover:border-[#1b2d5e] hover:bg-[#f9fafc]" : "cursor-not-allowed border-[#e5e8ef] text-[#a6adbf]"
                }`}
              >
                <Icon className="h-6 w-6" /> Add a {EVIDENCE[t].label.toLowerCase()}
                <input
                  type="file"
                  className="hidden"
                  accept={ACCEPT[t]}
                  disabled={!storageConfigured}
                  {...(t === "photo" || t === "video" ? { capture: "environment" as const } : {})}
                  onChange={(e) => {
                    addFile(t, e.target.files?.[0]);
                    e.target.value = "";
                  }}
                />
              </label>
            );
          })}
          {!storageConfigured && <p className="text-[13px] text-[#8189a0] sm:col-span-2">File uploads open soon.</p>}
        </div>
      )}

      {items.length > 0 && (
        <ul className="space-y-2">
          {items.map((i) => {
            const Icon = EVIDENCE[i.type].icon;
            return (
              <li key={i.localId} className="flex items-center justify-between gap-3 rounded-2xl bg-[#f9fafc] px-4 py-3 text-sm ring-1 ring-[#e5e8ef]">
                <span className="flex min-w-0 items-center gap-2 text-[#16213e]">
                  <Icon className="h-4 w-4 flex-shrink-0 text-[#5f6780]" />
                  <span className="truncate">{i.fileName ?? i.url ?? (i.text ? `${i.text.slice(0, 40)}…` : "")}</span>
                </span>
                {i.progress !== undefined && i.progress < 100 ? (
                  <span className="text-xs font-bold text-[#1b2d5e]">{i.progress}%</span>
                ) : (
                  <button
                    type="button"
                    onClick={() => setItems((xs) => xs.filter((x) => x.localId !== i.localId))}
                    className="rounded-lg p-1 text-[#b94a3a] hover:bg-[#fff5f3]"
                    aria-label="Remove"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {types.includes("text") && (
        <div>
          <label htmlFor="answer" className="mb-1.5 block text-sm font-semibold text-[#3b4560]">Your answer</label>
          <textarea id="answer" className={`${input} min-h-[120px]`} value={textDraft} onChange={(e) => setTextDraft(e.target.value)} placeholder="Type your answer here" />
        </div>
      )}
      {types.includes("link") && (
        <div>
          <label htmlFor="link" className="mb-1.5 block text-sm font-semibold text-[#3b4560]">Your link</label>
          <input id="link" className={input} value={linkDraft} onChange={(e) => setLinkDraft(e.target.value)} placeholder="https://" />
        </div>
      )}

      <div className="space-y-3 rounded-[22px] border border-[#e5e8ef] bg-white p-5">
        <p className="text-sm font-bold text-[#16213e]">Three quick questions</p>
        {([
          ["built", "What did I build or do?"],
          ["helps", "Who does it help?"],
          ["learned", "What did I learn?"],
        ] as const).map(([k, q]) => (
          <div key={k}>
            <label htmlFor={`r-${k}`} className="mb-1 block text-[13px] font-semibold text-[#5f6780]">{q}</label>
            <input id={`r-${k}`} className={input} value={reflection[k] ?? ""} onChange={(e) => setReflection((r) => ({ ...r, [k]: e.target.value }))} />
          </div>
        ))}
      </div>

      {msg && <p className="text-sm font-semibold text-[#b94a3a]">{msg}</p>}
      <button type="button" onClick={onSubmit} disabled={isLoading || uploading} className={`${primaryBtn} w-full`}>
        <Upload className="h-5 w-5" />
        {uploading ? "Uploading…" : isLoading ? "Handing in…" : "Hand it in"}
      </button>
    </div>
  );
}

/* ─── I'm stuck ──────────────────────────────────────────────── */

function StuckDialog({ childId, lesson, onClose }: { childId: string; lesson: LearnLesson; onClose: () => void }) {
  const [step, setStep] = useState(1);
  const [tried, setTried] = useState("");
  const [errorTxt, setErrorTxt] = useState("");
  const [lookedUp, setLookedUp] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [send, { isLoading }] = useSendStuckMutation();

  useEffect(() => {
    if (secondsLeft === null || secondsLeft <= 0) return;
    const t = setTimeout(() => setSecondsLeft((s) => (s ?? 1) - 1), 1000);
    return () => clearTimeout(t);
  }, [secondsLeft]);

  const onSend = async () => {
    try {
      const res = await send({ childId, lessonKey: lesson.key, tried, errorText: errorTxt || undefined, lookedUp }).unwrap();
      setMsg({ ok: true, text: res.message ?? "Sent" });
    } catch (err) {
      setMsg({ ok: false, text: errorText(err, "Could not send") });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-[#16213e]/40 p-4 sm:items-center" onClick={onClose}>
      <div className="w-full max-w-md rounded-[28px] bg-white p-6" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="I'm stuck">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-lg font-bold text-[#16213e]">Stuck? Let's work it out.</p>
            <p className="text-xs text-[#8189a0]">Step {Math.min(step, 3)} of 3. Builders try these first.</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-xl p-1.5 text-[#5f6780] hover:bg-[#f1f3f8]" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="mt-4">
          {msg?.ok ? (
            <>
              <p className="rounded-2xl bg-[#edfaf4] p-4 text-sm text-[#2a7a56]">{msg.text}</p>
              <button type="button" onClick={onClose} className={`${primaryBtn} mt-4 w-full`}>Close</button>
            </>
          ) : step === 1 ? (
            <>
              <label htmlFor="tried" className="mb-2 block text-sm font-semibold text-[#16213e]">1. What have you already tried?</label>
              <textarea id="tried" className={`${input} min-h-[90px]`} value={tried} onChange={(e) => setTried(e.target.value)} placeholder="I tried…" />
              <button type="button" disabled={tried.trim().length < 10} onClick={() => setStep(2)} className={`${primaryBtn} mt-4 w-full`}>Next</button>
            </>
          ) : step === 2 ? (
            <>
              <label htmlFor="err" className="mb-2 block text-sm font-semibold text-[#16213e]">2. What does the error or problem say?</label>
              <p className="mb-2 text-xs text-[#8189a0]">Copy the exact words if there are any. Read them slowly: they often tell you the answer.</p>
              <textarea id="err" className={`${input} min-h-[90px]`} value={errorTxt} onChange={(e) => setErrorTxt(e.target.value)} placeholder="(optional)" />
              <button type="button" onClick={() => setStep(3)} className={`${primaryBtn} mt-4 w-full`}>Next</button>
            </>
          ) : (
            <>
              <p className="mb-2 text-sm font-semibold text-[#16213e]">3. Look it up for 3 minutes</p>
              <p className="mb-3 text-xs text-[#8189a0]">Search for the problem, watch the part of the video you're stuck on again, or read the words.</p>
              {secondsLeft === null ? (
                <button type="button" onClick={() => setSecondsLeft(180)} className="w-full rounded-2xl border-2 border-[#c9cfe0] py-3 text-sm font-bold text-[#1b2d5e]">
                  Start the 3-minute timer
                </button>
              ) : (
                <p className="text-center text-3xl font-bold tabular-nums text-[#1b2d5e]">
                  {secondsLeft > 0 ? `${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, "0")}` : "Time's up!"}
                </p>
              )}
              <label className="mt-4 flex items-center gap-2 text-sm text-[#3b4560]">
                <input type="checkbox" className="h-4 w-4 accent-[#1b2d5e]" checked={lookedUp} onChange={(e) => setLookedUp(e.target.checked)} />
                I looked it up and I'm still stuck
              </label>
              {msg && <p className="mt-3 text-sm text-[#b94a3a]">{msg.text}</p>}
              <button type="button" onClick={onSend} disabled={isLoading || (!lookedUp && (secondsLeft ?? 1) > 0)} className={`${primaryBtn} mt-4 w-full`}>
                {isLoading ? "Sending…" : "Ask my tutor"}
              </button>
              <p className="mt-2 text-center text-[12.5px] text-[#8189a0]">Fixed it yourself? Brilliant, just close this.</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── The lesson, one step at a time ─────────────────────────── */

type Stage = "ready" | "watch" | "do" | "handin" | "after";

const STEPS: { id: Exclude<Stage, "after">; label: string; title: string }[] = [
  { id: "ready", label: "Get ready", title: "Before you watch" },
  { id: "watch", label: "Watch", title: "Watch the video" },
  { id: "do", label: "Do", title: "Do the activity" },
  { id: "handin", label: "Hand in", title: "Hand in your work" },
];

function startingStage(status: WorkStatus): Stage {
  if (status === "in_progress") return "do";
  if (status === "not_started") return "ready";
  return "after"; // submitted, needs_revision, accepted
}

function Stepper({ stage, onPick }: { stage: Stage; onPick: (s: Stage) => void }) {
  const at = stage === "after" ? STEPS.length : STEPS.findIndex((s) => s.id === stage);
  return (
    <ol className="flex items-center gap-1.5 sm:gap-2" aria-label="Lesson steps">
      {STEPS.map((s, i) => {
        const done = i < at;
        const current = i === at;
        return (
          <li key={s.id} className="flex min-w-0 flex-1 items-center gap-1.5 sm:gap-2">
            <button
              type="button"
              onClick={() => onPick(s.id)}
              aria-current={current ? "step" : undefined}
              className="flex min-w-0 items-center gap-2"
            >
              <span
                className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-[12.5px] font-bold transition ${
                  current ? "bg-[#1b2d5e] text-white" : done ? "bg-[#2a7a56] text-white" : "bg-[#ebeff7] text-[#5f6780]"
                }`}
              >
                {done ? <Check className="h-3.5 w-3.5" /> : i + 1}
              </span>
              <span className={`hidden truncate text-[13px] font-semibold sm:block ${current ? "text-[#16213e]" : "text-[#8189a0]"}`}>
                {s.label}
              </span>
            </button>
            {i < STEPS.length - 1 && <span className={`h-0.5 flex-1 rounded-full ${i < at ? "bg-[#2a7a56]" : "bg-[#e5e8ef]"}`} />}
          </li>
        );
      })}
    </ol>
  );
}

function LessonPlayer({
  childId, week, lesson, isParent,
}: { childId: string; week: LearnWeek; lesson: LearnLesson; isParent: boolean }) {
  const navigate = useNavigate();
  const [stage, setStage] = useState<Stage>(() => startingStage(lesson.work.status));
  const [doneSteps, setDoneSteps] = useState<boolean[]>(() => lesson.action.steps.map(() => false));
  const [stuck, setStuck] = useState(false);
  // In-app workspace: the project this lesson uses, whether it's open, and
  // whether it goes in with the work (on by default once it's been used here)
  const [project, setProject] = useState<string | null>(null);
  const [workspaceOpen, setWorkspaceOpen] = useState(false);
  const [handInProject, setHandInProject] = useState<string | null>(() => {
    const prev = lesson.work.attempts[lesson.work.attempts.length - 1]?.evidence.find((e) => e.type === "project");
    return prev?.projectId ?? null;
  });
  // Just handed in: show "Handed in" before the refreshed status arrives
  const [justHandedIn, setJustHandedIn] = useState(false);
  const [markSeen] = useMarkFeedbackSeenMutation();
  const [markWatched] = useMarkWatchedMutation();
  const watched = useRef(lesson.work.status !== "not_started");
  const topRef = useRef<HTMLDivElement>(null);

  const attempts = lesson.work.attempts;
  const last = attempts[attempts.length - 1];
  const status = lesson.work.status;
  const index = week.lessons.findIndex((l) => l.key === lesson.key);
  const nextLesson = week.lessons[index + 1] ?? null;
  const weekUrl = `/user/learn/${childId}/${week.key}`;

  // Opening feedback counts as reading it
  useEffect(() => {
    if (last?.review && (!lesson.work.feedbackSeenAt || lesson.work.feedbackSeenAt < last.review.reviewedAt)) {
      markSeen({ childId, weekKey: week.key, lessonKey: lesson.key });
    }
  }, [last?.review?.reviewedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  const go = (s: Stage) => {
    setStage(s);
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const onWatched = () => {
    if (watched.current) return;
    watched.current = true;
    markWatched({ childId, lessonKey: lesson.key });
  };

  const stepInfo = STEPS.find((s) => s.id === stage);
  const stepNo = STEPS.findIndex((s) => s.id === stage) + 1;

  // One main action per step, in the footer
  let primary: { label: string; icon: LucideIcon; onClick: () => void } | null = null;
  let back: Stage | null = null;
  if (stage === "ready") primary = { label: lesson.hasVideo ? "Watch the video" : "Read the lesson", icon: Play, onClick: () => go("watch") };
  if (stage === "watch") {
    primary = { label: "Next: the activity", icon: ArrowRight, onClick: () => { onWatched(); go("do"); } };
    back = "ready";
  }
  if (stage === "do") {
    primary = { label: "Hand in my work", icon: ListChecks, onClick: () => go("handin") };
    back = "watch";
  }
  if (stage === "handin") back = "do";

  return (
    <div ref={topRef} className="mx-auto max-w-3xl scroll-mt-24">
      {/* Lesson header */}
      <div className="flex items-start justify-between gap-3">
        <Link to={weekUrl} className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#5f6780] hover:text-[#16213e]">
          <ArrowLeft className="h-4 w-4" /> Week {week.calendarWeek}
        </Link>
        <button
          type="button"
          onClick={() => setStuck(true)}
          className="inline-flex items-center gap-1.5 rounded-xl border border-[#dde2ec] bg-white px-3 py-1.5 text-[13px] font-semibold text-[#3b4560] hover:bg-[#f1f3f8]"
        >
          <LifeBuoy className="h-4 w-4" /> I'm stuck
        </button>
      </div>
      <p className="mt-4 text-[12.5px] font-bold uppercase tracking-[0.12em] text-[#c9a227]">
        Lesson {lesson.order} of {week.lessons.length} · {lesson.strand}
      </p>
      <h1 className="mt-1 text-2xl font-extrabold leading-tight tracking-[-0.02em] text-[#16213e] sm:text-[1.75rem]">{lesson.title}</h1>

      <div className="mt-5">
        <Stepper stage={stage} onPick={go} />
      </div>

      {/* The one thing to do now */}
      <section className="mt-6 rounded-[28px] bg-white p-5 shadow-[0_12px_40px_rgba(27,45,94,0.08)] ring-1 ring-[#e5e8ef] sm:p-7">
        {stepInfo && (
          <p className="mb-4 text-[13px] font-bold uppercase tracking-[0.1em] text-[#5f6780]">
            Step {stepNo} · {stepInfo.title}
          </p>
        )}

        {stage === "ready" && <ReadyStep lesson={lesson} isParent={isParent} />}
        {stage === "watch" && <WatchStep childId={childId} lesson={lesson} onWatched={onWatched} />}
        {stage === "do" && (
          <DoStep
            lesson={lesson}
            done={doneSteps}
            setDone={setDoneSteps}
            workspace={
              lesson.workspace ? (
                <WorkspaceCard
                  childId={childId}
                  lessonKey={lesson.key}
                  kind={lesson.workspace}
                  chosen={project}
                  onChoose={setProject}
                  onOpen={(id) => {
                    setProject(id);
                    setWorkspaceOpen(true);
                  }}
                />
              ) : undefined
            }
          />
        )}
        {stage === "handin" && (
          <HandInStep
            key={`${lesson.key}-${attempts.length}`}
            childId={childId}
            weekKey={week.key}
            lesson={lesson}
            storageConfigured={week.storageConfigured}
            startFrom={status === "submitted" ? last : undefined}
            project={handInProject}
            setProject={setHandInProject}
            onHandedIn={() => {
              setJustHandedIn(true);
              go("after");
            }}
          />
        )}

        {stage === "after" && (
          <div className="space-y-5">
            {(status === "submitted" || (justHandedIn && status !== "accepted" && status !== "needs_revision")) && (
              <>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-8 w-8 flex-shrink-0 text-[#2a7a56]" />
                  <div>
                    <p className="text-xl font-bold text-[#16213e]">Handed in. Well done!</p>
                    <p className="mt-1 text-[15px] text-[#5f6780]">
                      Your tutor will look at it and send feedback, usually within 2 days.
                    </p>
                  </div>
                </div>
                {last && (
                  <div className="space-y-2 rounded-[20px] bg-[#f9fafc] p-4">
                    <p className="text-[13px] font-semibold text-[#5f6780]">What you handed in</p>
                    {last.evidence.map((e, i) => <MediaView key={i} childId={childId} lessonKey={lesson.key} ev={e} />)}
                  </div>
                )}
                <button type="button" onClick={() => go("handin")} className="text-[13px] font-semibold text-[#1b2d5e]">
                  Change what I handed in
                </button>
              </>
            )}
            {(status === "accepted" || status === "needs_revision") && last?.review && (
              <FeedbackCard review={last.review} childId={childId} lessonKey={lesson.key} />
            )}
            {status === "needs_revision" && (
              <button type="button" onClick={() => go("do")} className={`${primaryBtn} w-full`}>
                <RotateCcw className="h-5 w-5" /> Try again
              </button>
            )}
            {status !== "needs_revision" && (
              nextLesson ? (
                <button type="button" onClick={() => navigate(`${weekUrl}/${nextLesson.key}`)} className={`${primaryBtn} w-full`}>
                  Next lesson: {nextLesson.title} <ArrowRight className="h-5 w-5" />
                </button>
              ) : (
                <Link to={weekUrl} className={`${primaryBtn} w-full`}>
                  Back to the week <ArrowRight className="h-5 w-5" />
                </Link>
              )
            )}
          </div>
        )}
      </section>

      {/* Footer: back, and the one main action */}
      {(primary || back) && (
        <div className="sticky bottom-0 z-10 -mx-4 mt-5 flex items-center justify-between gap-3 border-t border-[#e5e8ef] bg-[#f5f4ef]/95 px-4 py-3 backdrop-blur sm:static sm:mx-0 sm:border-0 sm:bg-transparent sm:px-0 sm:py-0">
          {back ? (
            <button type="button" onClick={() => go(back!)} className={quietBtn}>
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
          ) : (
            <span />
          )}
          {primary && (
            <button type="button" onClick={primary.onClick} className={primaryBtn}>
              {primary.label} <primary.icon className="h-5 w-5" />
            </button>
          )}
        </div>
      )}

      {attempts.length > (status === "submitted" ? 1 : 0) && stage === "after" && (
        <details className="mt-5 rounded-[20px] border border-[#e5e8ef] bg-white px-5 py-3">
          <summary className="cursor-pointer text-sm font-semibold text-[#3b4560]">Earlier tries ({attempts.length})</summary>
          <div className="mt-3 space-y-4">
            {[...attempts].reverse().map((a) => (
              <div key={a.id} className="border-t border-[#ebeff7] pt-3">
                <p className="mb-2 text-xs font-bold text-[#8189a0]">
                  Try {a.number} · {new Date(a.submittedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                </p>
                <div className="space-y-2">
                  {a.evidence.map((e, i) => <MediaView key={i} childId={childId} lessonKey={lesson.key} ev={e} />)}
                </div>
                {a.review && (
                  <p className="mt-2 text-xs text-[#5f6780]">
                    {a.review.decision === "accepted" ? "Accepted" : "Try again"}
                    {a.review.nextStep ? `. ${a.review.nextStep}` : ""}
                  </p>
                )}
              </div>
            ))}
          </div>
        </details>
      )}

      {stuck && <StuckDialog childId={childId} lesson={lesson} onClose={() => setStuck(false)} />}

      {workspaceOpen && project && (
        <WorkspaceOverlay
          childId={childId}
          projectId={project}
          lesson={{ key: lesson.key, title: lesson.title, steps: lesson.action.steps }}
          done={doneSteps}
          setDone={setDoneSteps}
          onClose={() => {
            setWorkspaceOpen(false);
            setHandInProject(project); // worked on it here, so it goes in with the work
          }}
          onHandIn={(id) => {
            setWorkspaceOpen(false);
            setHandInProject(id);
            go("handin");
          }}
        />
      )}
    </div>
  );
}

/* ─── The week: its goal and its lessons ─────────────────────── */

/** The lesson to carry on with: unfinished work first, in order. */
export function nextLessonOf(week: LearnWeek) {
  return (
    week.lessons.find((l) => l.work.status === "needs_revision") ??
    week.lessons.find((l) => l.work.status === "in_progress" || l.work.status === "not_started") ??
    null
  );
}

function WeekOverview({ childId, week, isParent }: { childId: string; week: LearnWeek; isParent: boolean }) {
  const next = nextLessonOf(week);
  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <Link to={`/user/learn/${childId}`} className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#5f6780] hover:text-[#16213e]">
        <ArrowLeft className="h-4 w-4" /> All weeks
      </Link>

      <section className="rounded-[28px] bg-white p-6 shadow-[0_12px_40px_rgba(27,45,94,0.08)] ring-1 ring-[#e5e8ef] sm:p-7">
        <p className="text-[12.5px] font-bold uppercase tracking-[0.12em] text-[#c9a227]">
          Week {week.calendarWeek}
          {week.isCurrent ? " · this week" : ""}
          {week.weekEndsAt ? ` · ends ${fmtDay(week.weekEndsAt)}` : ""}
        </p>
        <h1 className="mt-1 text-2xl font-extrabold tracking-[-0.02em] text-[#16213e] sm:text-[1.75rem]">{week.title}</h1>
        {week.lessons.length > 0 && <div className="mt-4"><ProgressBar p={week.progress} /></div>}
        {next ? (
          <Link to={`/user/learn/${childId}/${week.key}/${next.key}`} className={`${primaryBtn} mt-5 w-full sm:w-auto`}>
            <Play className="h-5 w-5" />
            {next.work.status === "needs_revision" ? "Try again" : next.work.status === "in_progress" ? "Carry on" : "Start"}: {next.title}
          </Link>
        ) : week.lessons.length > 0 ? (
          <p className="mt-5 flex items-center gap-2 text-[15px] font-semibold text-[#2a7a56]">
            <CheckCircle2 className="h-5 w-5" /> Every lesson this week is handed in.
          </p>
        ) : null}
      </section>

      {week.onsiteSession && (
        <div className="flex gap-3 rounded-[22px] border border-[#d6efe2] bg-[#f3fbf7] px-5 py-4 text-sm text-[#1f5c3f]">
          <MapPin className="mt-0.5 h-5 w-5 flex-shrink-0" />
          <div>
            <p className="font-bold">
              On-site this week
              {week.onsiteSession.sessionDay ? `: ${week.onsiteSession.sessionDay}` : ""}
              {week.onsiteSession.sessionTime ? `, ${week.onsiteSession.sessionTime}` : ""}
            </p>
            <p className="mt-0.5">{[week.onsiteSession.name, week.onsiteSession.address].filter(Boolean).join(" · ") || week.onsiteSession.city}</p>
            <p className="mt-1 text-xs text-[#2a7a56]">Bring what you've made so far. The online lessons are the same as always.</p>
          </div>
        </div>
      )}

      {week.lessons.length === 0 ? (
        <p className="rounded-[24px] border border-dashed border-[#cdd4e2] bg-[#f9fafc] p-8 text-center text-sm text-[#5f6780]">
          This week's lessons are being prepared.
        </p>
      ) : (
        <ol className="space-y-2.5">
          {week.lessons.map((l) => {
            const st = STATUS[l.work.status];
            const isNext = l.key === next?.key;
            return (
              <li key={l.key}>
                <Link
                  to={`/user/learn/${childId}/${week.key}/${l.key}`}
                  className={`flex items-center gap-4 rounded-[20px] border bg-white p-4 transition hover:shadow-[0_8px_24px_rgba(27,45,94,0.08)] ${
                    isNext ? "border-[#1b2d5e]" : "border-[#e5e8ef]"
                  }`}
                >
                  <span
                    className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                      l.work.status === "accepted" ? "bg-[#2a7a56] text-white" : "bg-[#ebeff7] text-[#1b2d5e]"
                    }`}
                  >
                    {l.work.status === "accepted" ? <Check className="h-5 w-5" /> : l.order}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[15px] font-bold text-[#16213e]">{l.title}</span>
                    <span className="mt-0.5 flex items-center gap-1.5 text-[12.5px] text-[#8189a0]">
                      {l.strand} · <Clock className="h-3.5 w-3.5" /> {l.watchMinutes + l.doMinutes + l.uploadMinutes} min
                    </span>
                  </span>
                  <span className={`hidden flex-shrink-0 rounded-full px-2.5 py-1 text-[12px] font-bold sm:inline-block ${st.cls}`}>{st.label}</span>
                  <ArrowRight className="h-4 w-4 flex-shrink-0 text-[#a6adbf]" />
                </Link>
              </li>
            );
          })}
        </ol>
      )}

      <details className="rounded-[22px] border border-[#e5e8ef] bg-white px-5 py-4">
        <summary className="flex cursor-pointer items-center gap-2 text-sm font-bold text-[#16213e]">
          <Target className="h-4 w-4 text-[#1b2d5e]" /> This week's goal
        </summary>
        <ul className="mt-3 space-y-1.5 text-[15px] leading-6 text-[#16213e]">
          {week.weeklyTarget.must.map((m, i) => (
            <li key={i} className="flex gap-2"><Check className="mt-1 h-4 w-4 flex-shrink-0 text-[#2a7a56]" /> {m}</li>
          ))}
        </ul>
        {week.weeklyTarget.stretch.length > 0 && (
          <>
            <p className="mt-4 text-[13px] font-semibold text-[#5f6780]">Want more? Stretch goals</p>
            <ul className="mt-1.5 space-y-1.5 text-[15px] leading-6 text-[#3b4560]">
              {week.weeklyTarget.stretch.map((m, i) => (
                <li key={i} className="flex gap-2"><Star className="mt-1 h-4 w-4 flex-shrink-0 text-[#c9a227]" /> {m}</li>
              ))}
            </ul>
          </>
        )}
      </details>

      {isParent && (week.parentBrief || week.homeMaterials.length > 0) && (
        <details className="rounded-[22px] bg-[#fff8ee] px-5 py-4">
          <summary className="flex cursor-pointer items-center gap-2 text-sm font-bold text-[#7a4a10]">
            <Users className="h-4 w-4" /> For parents: this week, and what to get ready
          </summary>
          {week.parentBrief && <p className="mt-2 text-sm leading-6 text-[#5b3a10]">{week.parentBrief}</p>}
          {week.homeMaterials.length > 0 && (
            <ul className="mt-2 list-disc pl-5 text-sm leading-6 text-[#5b3a10]">
              {week.homeMaterials.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          )}
        </details>
      )}
    </div>
  );
}

/* ═══ PAGE ═══════════════════════════════════════════════════════ */

export default function UserLearnWeek() {
  const { childId = "", weekKey = "", lessonKey } = useParams();
  const [params] = useSearchParams();
  const user = useSelector(selectUser);
  const childMode = useChildMode();
  // In child mode the parent's device shows the child's view (no parent notes)
  const isParent = user?.role === "parent" && !childMode;
  const { data, isLoading, error } = useGetLearnWeekQuery({ childId, weekKey });
  const week = data?.data;
  const selected = useMemo(() => week?.lessons.find((l) => l.key === lessonKey) ?? null, [week, lessonKey]);

  if (isLoading)
    return (
      <div className="mx-auto max-w-3xl space-y-4">
        <div className="h-8 w-40 animate-pulse rounded-xl bg-[#ebeff7]" />
        <div className="h-72 animate-pulse rounded-[28px] bg-[#ebeff7]" />
      </div>
    );
  if (error || !week)
    return (
      <div className="mx-auto max-w-3xl space-y-4">
        <Link to={`/user/learn/${childId}`} className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#5f6780]">
          <ArrowLeft className="h-4 w-4" /> Back
        </Link>
        <p className="rounded-[24px] border border-[#f5d9a0] bg-[#fff8ee] p-6 text-sm text-[#7a4a10]">{errorText(error, "Could not load this week")}</p>
      </div>
    );

  // "Continue" from the journey: straight into the lesson to carry on with
  if (!lessonKey && params.get("next") === "1") {
    const next = nextLessonOf(week);
    if (next) return <Navigate to={`/user/learn/${childId}/${week.key}/${next.key}`} replace />;
  }

  if (lessonKey && selected) {
    return <LessonPlayer key={selected.key} childId={childId} week={week} lesson={selected} isParent={isParent} />;
  }
  return <WeekOverview childId={childId} week={week} isParent={isParent} />;
}
